/**
 * The choices behind the Settings page's dropdowns: currency, country,
 * state/province and payment terms.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Derived from `Intl`, not hand-typed.
 * ─────────────────────────────────────────────────────────────────────────
 * A hand-written table of 180 currencies and 249 countries is a table that is
 * wrong somewhere and stays wrong: names change (Turkey → Türkiye, Czech
 * Republic → Czechia, Swaziland → Eswatini), and nobody revisits a constants
 * file to find out. The runtime already ships that data and keeps it current,
 * so the only thing kept here is the ISO 3166-1 alpha-2 CODE list — codes are
 * stable in a way display names are not.
 *
 * WHAT IS STORED, and why it matters which. Currency stores the ISO 4217
 * CODE ("EUR"): three letters that mean the same thing on every runtime, and
 * what crm-api's three-letter rule validates. Country stores the display NAME
 * ("Netherlands"), because that is the column's existing contract — the
 * default in the database is the string "U.S.A" — and because it is printed
 * verbatim on an invoice. A name that drifts between ICU versions therefore
 * cannot corrupt anything: the stored string is printed as it was saved, and
 * simply stops matching a list entry. Which is why every one of these
 * dropdowns is a COMBO BOX, open to a value it does not offer.
 */

/** One currency, as the dropdown shows it. */
export interface CurrencyOption {
  /** ISO 4217, and what gets stored. */
  code: string;
  /** The runtime's name for it — a label only. */
  name: string;
}

/**
 * The currencies XOP's resellers actually trade in, first. The rest follow
 * alphabetically; a searchable list still benefits from putting the likely
 * answer at the top, because that is what someone scrolling rather than
 * typing sees.
 */
const COMMON_CURRENCIES = [
  "USD", "EUR", "GBP", "CHF", "CAD", "AUD", "NZD", "SEK", "NOK", "DKK",
  "PLN", "CZK", "JPY", "CNY", "INR", "SGD", "HKD", "AED", "ZAR", "BRL",
] as const;

/** ISO 3166-1 alpha-2. The one table worth keeping, because codes do not
 * change under you the way names do. */
const REGION_CODES =
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ " +
  "BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
  "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ " +
  "DE DJ DK DM DO DZ EC EE EG EH ER ES ET " +
  "FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY " +
  "HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT " +
  "JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ " +
  "LA LB LC LI LK LR LS LT LU LV LY " +
  "MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ " +
  "NA NC NE NF NG NI NL NO NP NR NU NZ OM " +
  "PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW " +
  "SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ " +
  "TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ " +
  "UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW";

/**
 * The circulating ISO 4217 codes, from the runtime.
 *
 * `Intl.supportedValuesOf("currency")` is ES2022 — present in Node 18+ and
 * every current browser, and already free of the non-currency codes (XXX, the
 * metals, the test code). The fallback is the common set: an engine without
 * it would otherwise offer an empty dropdown, which is worse than a short one.
 */
function allCurrencyCodes(): string[] {
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.("currency");
    if (supported?.length) return supported;
  } catch {
    /* Fall through to the common set below. */
  }
  return [...COMMON_CURRENCIES];
}

function displayName(kind: "currency" | "region", code: string): string | null {
  try {
    const name = new Intl.DisplayNames(["en"], { type: kind }).of(code);
    // `of` echoes the code back when it knows nothing about it.
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

let currencyCache: CurrencyOption[] | null = null;

/**
 * Every ISO 4217 code the runtime knows, common ones first.
 *
 * Memoised: `Intl.DisplayNames` is not free, and this is called on every
 * render of a form that re-renders on each keystroke.
 */
export function currencyOptions(): CurrencyOption[] {
  if (currencyCache) return currencyCache;

  const common = new Set<string>(COMMON_CURRENCIES);
  const rest = allCurrencyCodes()
    .filter((code) => !common.has(code))
    .sort();

  currencyCache = [...COMMON_CURRENCIES, ...rest].map((code) => ({
    code,
    name: displayName("currency", code) ?? code,
  }));
  return currencyCache;
}

let countryCache: string[] | null = null;

/** Country names, alphabetical. What gets stored — see the header. */
export function countryOptions(): string[] {
  if (countryCache) return countryCache;

  countryCache = REGION_CODES.split(" ")
    .map((code) => displayName("region", code))
    .filter((name): name is string => Boolean(name))
    .sort((a, b) => a.localeCompare(b));
  return countryCache;
}

/**
 * States, provinces and territories — for the three countries where the
 * subdivision is a closed list people actually expect to pick from.
 *
 * Deliberately not "every country's subdivisions". Most countries either have
 * no such concept on a billing address or have one nobody fills in from a
 * list, and a dropdown that offers the wrong taxonomy is worse than a text
 * box. Everywhere else this returns nothing and the field stays free text.
 */
const SUBDIVISIONS: Record<string, readonly string[]> = {
  "United States": [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "District of Columbia", "Florida", "Georgia",
    "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
    "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina",
    "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
    "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas",
    "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin",
    "Wyoming", "Puerto Rico",
  ],
  Canada: [
    "Alberta", "British Columbia", "Manitoba", "New Brunswick",
    "Newfoundland and Labrador", "Northwest Territories", "Nova Scotia",
    "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan",
    "Yukon",
  ],
  Australia: [
    "Australian Capital Territory", "New South Wales", "Northern Territory",
    "Queensland", "South Australia", "Tasmania", "Victoria",
    "Western Australia",
  ],
};

/**
 * The spellings that reach this from real data. `resellers.country` defaults
 * to the literal string "U.S.A" in crm-api, so the most common value in the
 * database is one no standard list contains — without this the state dropdown
 * would be empty for exactly the resellers most likely to want it.
 */
const COUNTRY_ALIASES: Record<string, string> = {
  "u.s.a": "United States",
  "u.s.a.": "United States",
  usa: "United States",
  us: "United States",
  "united states of america": "United States",
  uk: "United Kingdom",
  "great britain": "United Kingdom",
  ca: "Canada",
  au: "Australia",
};

/** The canonical country name for a stored value, or the value itself. */
export function canonicalCountry(country: string | null | undefined): string {
  const raw = country?.trim() ?? "";
  return COUNTRY_ALIASES[raw.toLowerCase()] ?? raw;
}

/** Subdivisions to offer for a country, or none when there is no list worth
 * offering. */
export function subdivisionOptions(country: string | null | undefined): readonly string[] {
  return SUBDIVISIONS[canonicalCountry(country)] ?? [];
}

/**
 * What a reseller that has chosen nothing is OFFERED — in the Settings form,
 * where it is visible and editable before it is saved.
 *
 * Deliberately not a database default and deliberately not backfilled onto
 * existing rows. "Net 30" is a commercial promise about when a customer has
 * to pay; a reseller that has never opened Settings has not made it, and
 * writing it onto their invoices on their behalf is not a default, it is an
 * invention. Same reason the document renderer prints nothing for a blank
 * term rather than assuming one.
 *
 * So the form pre-fills, the reseller sees it in the field, and saving is
 * what makes it theirs.
 */
export const DEFAULT_CURRENCY = "USD";
export const DEFAULT_PAYMENT_TERMS = "Net 30";

/**
 * Payment terms people actually write on invoices. Open to anything else —
 * "50% on order, balance on delivery" is a real term and no list predicts it.
 */
export const PAYMENT_TERM_OPTIONS: readonly string[] = [
  "Due on receipt",
  "Net 7",
  "Net 14",
  "Net 15",
  "Net 30",
  "Net 45",
  "Net 60",
  "Net 90",
  "50% upfront, balance on delivery",
  "Payment in advance",
];
